import isEmpty from 'lodash/isEmpty'
import * as React from 'react'

import { FormItem } from 'main/components/common/Form'
import { Button, PhoneTextField, spacing, TextField } from 'main/ui'
import { TFunction, useTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE } from '../../../../../../common'
import { i18nKeyPrefix } from '../../../../i18n'
import { ScheduleTourModal_user } from '../../../common/gql'
import { ErrorText } from './ConfirmForm.styles'
import NoShowPolicy from './NoShowPolicy'
import { IParams, IValidation } from './types'
import { initialParams, validate } from './util'

const PRE = `${i18nKeyPrefix}-SCHEDULE-MODAL-REVIEW-CONFIRM`

interface IProps {
  error: string | null
  loading: boolean
  user: ScheduleTourModal_user
  onRequestReset: () => void
  onRequestSubmit: (params: IParams) => void
}

export default function ConfirmForm(props: IProps) {
  const { error, loading, user } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  const [params, setParams] = React.useState<IParams>(initialParams(user))
  const [validation, setValidation] = React.useState<IValidation>({})

  return (
    <div>
      {!user.hasPassword && (
        <div css={{ marginBottom: spacing.majorScale(4) }}>
          <FormItem label={t(`${PRE}-ITEM-PASSWORD-LABEL`)}>
            <TextField
              type="password"
              label={t(`${PRE}-PASSWORD-LABEL`)}
              placeholder={t(`${PRE}-PASSWORD-PLACEHOLDER`)}
              value={params.password}
              onChange={password => setParams(prev => ({ ...prev, password }))}
              validationHint={validation.password}
              required
            />
          </FormItem>
        </div>
      )}

      <FormItem label={t(`${PRE}-ITEM-LABEL`)}>
        <PhoneTextField
          label={t(`${PRE}-PHONE-LABEL`)}
          placeholder={t(`${PRE}-PHONE-PLACEHOLDER`)}
          value={params.phone}
          onChange={phone => setParams(prev => ({ ...prev, phone }))}
          validationHint={validation.phone}
          required
        />
      </FormItem>

      <div css={{ marginTop: spacing.majorScale(4) }}>
        <NoShowPolicy
          agreed={params.noShowPolicyAgreed}
          validationHint={validation.noShowPolicyAgreed}
          onChange={noShowPolicyAgreed => setParams(prev => ({ ...prev, noShowPolicyAgreed }))}
        />
      </div>

      {error && (
        <div css={{ marginTop: spacing.majorScale(2) }}>
          <ErrorText>{error}</ErrorText>
        </div>
      )}

      <div css={{ marginTop: spacing.majorScale(error ? 2 : 4) }}>
        <Button
          full
          disabled={loading}
          onClick={() => handleSubmit(props, params, setValidation, t)}
        >
          {loading ? t(`${PRE}-LOADING`) : t(`${PRE}-CONFIRM`)}
        </Button>
      </div>
    </div>
  )
}

function handleSubmit(
  props: IProps,
  params: IParams,
  setValidation: (validation: IValidation) => void,
  t: TFunction,
) {
  const { onRequestReset, onRequestSubmit, user } = props

  const skipPasswordValidation = !showPasswordSection(user)
  const validation = validate(params, skipPasswordValidation, t)
  setValidation(validation)
  onRequestReset()

  if (isEmpty(validation)) {
    onRequestSubmit(params)
  }
}

function showPasswordSection(user: ScheduleTourModal_user) {
  return !user.hasPassword
}
